import BaseApi, { FetchMethodType } from 'api/BaseApi';
import { PreliminaryCheckResult } from 'enums/curo/PreliminaryCheckResult';
import { PrescreenedOfferType } from 'enums/curo/PrescreenedOfferType';
import { PrescreenedPreliminaryCheckResult } from 'enums/curo/PrescreenedPreliminaryCheckResult';
import { TableValue } from 'product_modules/api/Types';

export enum ExistingApplicationStatus {
  Declined = 'Declined',
  Funded = 'Funded',
  InProgress = 'InProgress',
  NotFound = 'NotFound',
  NewApplication = 'NewApplication',
  RefinanceFunded = 'RefinanceFunded',
}

export interface ISubmitMailOfferRequest {
  borrowerEmail: string;
  borrowerPhone: string;
  borrowerLast6DigitsSSN: string;
  borrowerZipOrPostalCode: string;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
}

export interface ISubmitMailOfferResponse {
  actionsType: PrescreenedOfferType;
  decisionId: string;
  branchAddress?: string | null;
  branchCity?: string | null;
  branchState?: string | null;
  branchZip?: string | null;
  branchPhone?: string;
  offerAmount?: string;
  firstName?: string;
  summitEncryptedLeadId?: string;
}

export interface ICheckPrescreenShortPageOfferRequest {
  snowFlakeBorrowerMailType: string;
  snowFlakeBorrowerMailDate: string;
  borrowerEmail: string;
  borrowerPhone: string;
  borrowerLast6DigitsSSN: string;
  borrowerZipOrPostalCode: string;
  snowFlakeBranchAddress: string;
  snowFlakeBranchCity: string;
  snowFlakeBranchState: string;
  snowFlakeBranchZip: string;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
}

export interface ICheckPrescreenShortPageOfferResponse {
  prescreenedOfferType: PrescreenedOfferType;
}

export interface IExistingApplicationCheckResponse {
  nextStep: string | null;
  isNew?: boolean;
}

export interface IExistingBorrowerRequest {
  borrowerPersonalIdNumber: string;
  borrowerPhone: string;
  borrowerEmail: string;
}

export enum ExistingBorrowerStatus {
  Exists = 'Exists',
  NotFound = 'NotFound',
  Locked = 'Locked',
  PhoneNumberInUse = 'PhoneNumberInUse',
  EmailInUse = 'EmailInUse',
  AccountWithDifferentPhoneNumber = 'AccountWithDifferentPhoneNumber',
  AccountWithDifferentEmail = 'AccountWithDifferentEmail',
}

export interface IExistingBorrowerResponse {
  borrowerId: string | null;
  existingBorrowerStatus: ExistingBorrowerStatus;
}

export interface ICreateOrUpdateBorrowerRequest {
  borrowerFirstName: string;
  borrowerLastName: string;
  borrowerSSN: string;
  borrowerDateOfBirth: string;
  borrowerStreetAddress: string;
  borrowerCity: string;
  borrowerZipOrPostalCode: string;
  borrowerState: string;
  borrowerEmail: string;
  borrowerPhone: string;
  borrowerMarketingMessagesAccepted: boolean;
}

export interface ICreateOrUpdateBorrowerResponse {
  borrowerId: string;
}

export interface ICreateApplicationRequest {
  borrowerId: string;
  preliminaryCheckResult?: PreliminaryCheckResult | null;
  snowFlakeOfferCode?: string;
  snowFlakeBranchId?: string;
  snowFlakeBranchAddress?: string;
  snowFlakeBranchCity?: string;
  snowFlakeBranchState?: string;
  snowFlakeBranchZip?: string;
  snowFlakeBranchPhone?: string;
  snowFlakeBranchEmailAddress?: string;
  snowFlakeMailDate?: string;
  snowFlakeBorrowerMailType?: string;
  snowFlakeBorrowerOfferAmount?: string;
  snowFlakeExpirationDate?: string;
  snowFlakeBorrowerFirstName?: string;
  snowFlakeBorrowerLastName?: string;
  snowFlakeBorrowerStatus?: string;
  snowFlakeBorrowerAddress?: string;
  snowFlakeBorrowerCity?: string;
  snowFlakeBorrowerState?: string;
  snowFlakeBorrowerZip?: string;
  snowFlakeProductType?: string;
  snowFlakeBorrowerContractId?: string;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  payoffAmount?: number | null;
  gpsLoanTypeTable?: TableValue | null;
  gpsGetLoansStrategyVersion?: string | null;
  everHadLargeLoan?: boolean;
  borrowerStatus?: string | null;
  origGpsLoanOrAccountNumber?: string | null;
  gpsBorrowerActiveLoanWithCuro?: boolean;
  originalGpsLoanOpenDate?: string | null;
  currentLoanAmount?: number;
  origGpsLoanBranchId?: string | null;
  gpsDaysPastDue?: number;
  gpsSecurityCode?: number | null;
  gpsCurrentLoanType?: number | null;
  prescreenedOfferType?: PrescreenedOfferType;
}

export interface ICreateApplicationResponse {
  applicationId: string;
  applicationDisplayId: string;
}

export interface ICheckExistingAccountResponse {
  isAccountExists: boolean;
  isEmailVerified: boolean;
  isPhoneVerified: boolean;
}

export interface ICreateAccountRequest {
  borrowerId: string;
  borrowerEmail: string;
  borrowerPhone: string;
}

export interface IAuthResponse {
  accessToken: string;
  refreshToken: string;
}

export interface IBorrowerStateCheckResponse {
  passed: boolean;
}

export interface ISnowflakeShortPageDMCheckRequest {
  borrowerLast6DigitsSSN: string;
  borrowerZipOrPostalCode: string;
  borrowerPhone: string;
  borrowerEmail: string;
}

export interface ISnowflakePrescreenedOfferQueryRequest {
  borrowerFirstName: string;
  borrowerLastName: string;
  borrowerPersonalIdNumber: string;
  borrowerDateOfBirth: string;
  borrowerStreetAddress: string;
  borrowerZipOrPostalCode: string;
}

export interface ISnowflakePrescreenedOfferQueryResponse {
  snowFlakeOfferCode: string;
  snowFlakeBranchId: string;
  snowFlakeBranchAddress: string;
  snowFlakeBranchCity: string;
  snowFlakeBranchState: string;
  snowFlakeBranchZip: string;
  snowFlakeBranchPhone: string;
  snowFlakeMailDate: string;
  snowFlakeExpirationDate: string;
}

export interface ISnowflakeZipCodeCheckRequest {
  borrowerZipOrPostalCode: string;
}

export interface ISnowflakeZipCodeCheckResponse {
  snowFlakeBranchId: string;
  snowFlakeBranchAddress: string;
  snowFlakeBranchCity: string;
  snowFlakeBranchState: string;
  snowFlakeBranchZip: string;
  snowFlakeBranchPhone: string;
}

export interface ISnowflakeBranchIdMismatchCheckRequest {
  origGpsLoanBranchId: string;
}

export interface ISnowflakeBranchIdMismatchCheckResponse {
  snowFlakeBranchId: string;
  snowFlakeBranchAddress: string;
  snowFlakeBranchCity: string;
  snowFlakeBranchState: string;
  snowFlakeBranchZip: string;
}

export interface ISnowflakePrescreenedOfferCheckRequest {
  snowflakeBorrowerOfferCode: string;
  borrowerStreetAddress: string;
  borrowerCity: string;
  borrowerZipOrPostalCode: string;
  borrowerState: string;
  snowFlakeMailDate: string;
}

export interface ISnowflakeBorrowerStatusCheckRequest {
  borrowerPersonalIdNumber: string;
}

export interface ISnowflakeBorrowerStatusCheckResponse {
  snowFlakeBorrowerContractId: string;
  snowFlakeBorrowerStatus: string;
  snowFlakeProductType: string;
}

export interface IRunGpsCheckRequest {
  prescreenedPreliminaryCheckResult: PrescreenedPreliminaryCheckResult | null,
  borrowerPersonalIdNumber: string;
  snowflakeBorrowerOfferCode: string | null;
  snowflakeBorrowerBranchId: string | null;
  snowflakeBorrowerStatus: string | null;
  snowFlakeProductType: string | null;
  snowFlakeBorrowerContractId: string | null;
}

export interface IRunGpsCheckResponse {
  preliminaryCheckResult: PreliminaryCheckResult | null;
  gpsBorrowerActiveLoanWithCuro: boolean;
  currentLoanAmount: number;
  gpsBorrowerPriorChargeOffWithCuro: boolean;
  gpsDaysPastDue: number;
  gpsGetLoansStatusCode: string;
  origGpsLoanBranchId: string | null; 
  origGpsLoanOrAccountNumber: string | null; 
  originalGpsLoanOpenDate: string | null;
  payoffAmount: number | null;
  gpsLoanTypeTable: TableValue | null;
  gpsGetLoansStrategyVersion: string | null;
  everHadLargeLoan: boolean;
  borrowerStatus: string | null;
  gpsSecurityCode: number | null;
  gpsCurrentLoanType: number | null;
}

export interface IUtmDataValues {
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
}

export interface IRunSummitLeadIntegrationDMStrategyRequest {
  borrowerEmail: string;
  borrowerPhone: string;
  snowflakeBorrowerBranchId?: string;
  snowFlakeBorrowerOfferAmount?: string;
  snowFlakeBorrowerFirstName?: string;
  snowFlakeBorrowerLastName?: string;
  snowFlakeBorrowerAddress?: string;
  snowFlakeBorrowerCity?: string;
  snowFlakeBorrowerState?: string;
  snowFlakeBorrowerZip?: string;
}

export interface ICreateAssessmentRequest {
  token: string;
  action: string;
}

export interface ICreateAssessmentResponse {
  score: number;
  isValid: boolean;
}

interface IApplyProcessService {
  submitMailOffer(
    body: ISubmitMailOfferRequest,
  ):Promise<ISubmitMailOfferResponse>;

  existingApplicationCheck(
    borrowerPersonalIdNumber: string,
  ): Promise<IExistingApplicationCheckResponse>;

  existingBorrowerCheck(body: IExistingBorrowerRequest): Promise<IExistingBorrowerResponse>;
  // CURO-TODO: Add return type
  createBorrower(body: ICreateOrUpdateBorrowerRequest): Promise<ICreateOrUpdateBorrowerResponse>;
  updateBorrower(
    borrowerId: string,
    body: ICreateOrUpdateBorrowerRequest,
  ): Promise<ICreateOrUpdateBorrowerResponse>;

  createApplication(body: ICreateApplicationRequest): Promise<ICreateApplicationResponse>;

  checkExistingAccount(borrowerId: string): Promise<ICheckExistingAccountResponse>;

  createAccount(body: ICreateAccountRequest): Promise<IAuthResponse>;

  borrowerStateCheck(borrowerState: string): Promise<IBorrowerStateCheckResponse>;
}

export default class ApplyProcessApi extends BaseApi implements IApplyProcessService {
  async submitMailOffer(body: ISubmitMailOfferRequest): Promise<ISubmitMailOfferResponse> {
    const response = await this.fetch<ISubmitMailOfferResponse>(
      '/apply-process/submit-mail-offer',
      {
        body,
        method: FetchMethodType.POST,
      },
    );

    return response;
  }

  async existingApplicationCheck(
    borrowerPersonalIdNumber: string,
  ): Promise<IExistingApplicationCheckResponse> {
    const response = await this.fetch<IExistingApplicationCheckResponse>(
      '/apply-process/existing-application-check',
      {
        body: { borrowerPersonalIdNumber },
        method: FetchMethodType.POST,
      },
    );

    return response;
  }

  public async borrowerStateCheck(borrowerState: string): Promise<IBorrowerStateCheckResponse> {
    const response = await this.fetch<IBorrowerStateCheckResponse>(
      '/apply-process/borrower-state-check',
      {
        method: FetchMethodType.POST,
        body: { borrowerState },
      },
    );

    return response;
  }

  public async existingBorrowerCheck(
    body: IExistingBorrowerRequest,
  ): Promise<IExistingBorrowerResponse> {
    const response = await this.fetch<IExistingBorrowerResponse>(
      '/apply-process/existing-borrower-check',
      {
        body,
        method: FetchMethodType.POST,
      },
    );

    return response;
  }

  public async createBorrower(
    body: ICreateOrUpdateBorrowerRequest,
  ): Promise<ICreateOrUpdateBorrowerResponse> {
    const response = await this.fetch<ICreateOrUpdateBorrowerResponse>(
      '/apply-process/create-borrower',
      {
        body,
        method: FetchMethodType.POST,
      },
    );

    return response;
  }

  public async updateBorrower(
    borrowerId: string,
    body: ICreateOrUpdateBorrowerRequest,
  ): Promise<ICreateOrUpdateBorrowerResponse> {
    const response = await this.fetch<ICreateOrUpdateBorrowerResponse>(
      `/apply-process/update-borrower/${borrowerId}`,
      {
        body,
        method: FetchMethodType.PUT,
      },
    );

    return response;
  }

  public async createApplication(
    body: ICreateApplicationRequest,
  ): Promise<ICreateApplicationResponse> {
    const response = await this.fetch<ICreateApplicationResponse>(
      '/apply-process/create-application',
      {
        body,
        method: FetchMethodType.POST,
      },
    );

    return response;
  }

  public async checkExistingAccount(borrowerId: string): Promise<ICheckExistingAccountResponse> {
    const response = await this.fetch<ICheckExistingAccountResponse>(
      `/apply-process/check-existing-account/${borrowerId}`,
    );

    return response;
  }

  public async createAccount(body: ICreateAccountRequest): Promise<IAuthResponse> {
    const response = await this.fetch<IAuthResponse>('/apply-process/create-account', {
      body,
      method: FetchMethodType.POST,
    });

    return response;
  }

  public async createAssessment(
    body: ICreateAssessmentRequest,
  ): Promise<ICreateAssessmentResponse> {
    const response = await this.fetch<ICreateAssessmentResponse>(
      '/apply-process/create-assessment',
      {
        body,
        method: FetchMethodType.POST,
      },
    );

    return response;
  }
}
